import { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import AddDepositForm from "../../forms/payment/AddDeposite";
import { GetUser, GetPayment } from "../../Services";

const DriverPayment = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ✅ Fetch Admin Payment History
  const fetchAdminPayments = async () => {
    try {
      setLoading(true);
      const res = await GetPayment.fetchAdminPayment();
      setPayments(res?.payments || []);
      setFilteredPayments(res?.payments || []);
    } catch (error) {
      console.error("Failed to fetch admin payments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminPayments();
    fetchDrivers();
  }, []);

  // ✅ Filter payments by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredPayments(payments);
      return;
    }
    const lower = searchTerm.toLowerCase();
    const filtered = payments.filter(
      (p) =>
        p.transaction_id.toLowerCase().includes(lower) ||
        p.driver.name.toLowerCase().includes(lower) ||
        p.payment_mode.toLowerCase().includes(lower) ||
        p.status.toLowerCase().includes(lower)
    );
    setFilteredPayments(filtered);
  }, [searchTerm, payments]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const data = await GetUser.fetchDriverList();

      setDrivers(Array.isArray(data.drivers) ? data.drivers : []);
      // console.log("DOcs: ", data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeposite = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const depositeData = {
      driver_id: formData.get("driver_id"),
      amount: parseFloat(formData.get("amount")),
      payment_mode: formData.get("payment_mode"),
      transaction_id: formData.get("transaction_id"),
      status: formData.get("status"),
    };

    try {
      await GetPayment.createDeposite(depositeData);
      fetchAdminPayments(); // Refresh payment list after adding
      setShowModal(false); // Close modal
    } catch (error) {
      console.error("Error creating deposite:", error);
    }
  };

  const handleOnselect = async (id) => {
    try {
      if (!id) {
        fetchAdminPayments();
        return;
      }
      const res = await GetPayment.fetchDriverPaymentByID(id);
      if (res) {
        setPayments(res || []);
        setFilteredPayments(res || []);
      }
    } catch (error) {
      console.error("Error fetching payment by ID:", error);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <h2 className="text-2xl font-bold">Driver Payment History</h2>
        </div>

        <div className="card">
          <div className="card-header row">
            <button
              type="button"
              className="btn btn-primary btn-sm flex items-center col-sm-6"
              onClick={() => setShowModal(true)}
            >
              <i className="fa fa-plus mr-1"></i> Add Deposite
            </button>
            <select
              name="driver_id"
              className="form-control col-sm-6"
              onChange={(e) => handleOnselect(e.target.value)}
            >
              <option value="" disabled>
                Select Driver
              </option>
              <option value="">All Drivers</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          <Modal showModal={showModal} setShowModal={setShowModal}>
            <AddDepositForm />
          </Modal>

          <div className="card-body flex flex-col gap-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-4">
                <button type="button" className="btn btn-outline-secondary">
                  CSV
                </button>
                <button type="button" className="btn btn-outline-secondary">
                  Excel
                </button>
                <button type="button" className="btn btn-outline-secondary">
                  PDF
                </button>
              </div>

              <input
                type="text"
                className="form-control"
                placeholder="Search by transaction, driver, or mode..."
                style={{ width: "300px" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="mt-4">
              {loading ? (
                <p>Loading payment history...</p>
              ) : filteredPayments.length === 0 ? (
                <p>No payment records found.</p>
              ) : (
                <table className="table table-bordered w-full">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Transaction ID</th>
                      <th>Type</th>
                      <th>Tran. Amount</th>
                      <th>Prev. Amount</th>
                      <th>Total Amount</th>
                      <th>Comm. Amount</th>
                      <th>Driver Name</th>
                      <th>Payment Mode</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((p, index) => (
                      <tr key={p.id}>
                        <td>{index + 1}</td>
                        <td>{p.transaction_id}</td>
                        <td>{p.transaction_type}</td>
                        <td>{p.transaction_amount}</td>
                        <td>{p.previous_amount}</td>
                        <td>{p.total_amount}</td>
                        <td>{p.commission_amount}</td>
                        <td>{p.driver?.name || "N/A"}</td>
                        <td>{p.payment_mode}</td>
                        <td>{p.status}</td>
                        <td>
                          {new Date(p.transaction_date).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer clearfix">
              <ul className="pagination pagination-sm m-0 float-right">
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) handlePageChange(currentPage - 1);
                    }}
                  >
                    &laquo;
                  </a>
                </li>
                {[...Array(totalPages).keys()].map((page) => (
                  <li
                    className={`page-item ${
                      currentPage === page + 1 ? "active" : ""
                    }`}
                    key={page + 1}
                  >
                    <a
                      className="page-link"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page + 1);
                      }}
                    >
                      {page + 1}
                    </a>
                  </li>
                ))}
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages)
                        handlePageChange(currentPage + 1);
                    }}
                  >
                    &raquo;
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* <div className="card-footer clearfix">
            <span className="text-muted text-sm">
              Showing {filteredPayments.length} of {payments.length} records
            </span>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default DriverPayment;
